/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { SignupService, UserService } from '@services';
import utils from '@utils/utils';
import ReactGA from 'react-ga';

import './ContactInfo.scss';

// Custom method to check if passwords match in Yup validation
function matchPass(ref, minLength, message = '') {
	return this.test({
		name      : 'matchPass',
		exclusive : false,
		message,
		test(value) {
			// Only evaluate if the password minimum length condition is met
			if (this.resolve(ref).length >= minLength) {
				return value === this.resolve(ref);
			}
			// Otherwise, always return true
			return true;
		},
	});
}

// Add the custom method to be used on string fields in validation
Yup.addMethod(Yup.string, 'matchPass', matchPass);

const SignupSchema = Yup.object().shape({
	FirstName : Yup.string().trim().required().label('First Name'),
	LastName  : Yup.string().trim().required().label('Last Name'),
	Email     : Yup.string().trim().required().email('Invalid email format')
		.label('Email/Username'),
	MobilePhone : Yup.string().trim().required().matches(/^([\d]{3})-([\d]{3})-([\d]{4})$/gi, 'Invalid phone number')
		.label('Phone Number'),
	Password : Yup.string().trim().required().min(6, 'Password must be at least 6 characters long')
		.default('')
		.label('Password'),
	ConfirmPassword : Yup.string().trim().matchPass(Yup.ref('Password'), 6, 'Passwords don\'t match'),
});

class ContactInfo extends Component {
	constructor(props) {
		super(props);
		this.state = {
			showPassword    : false,
			showConfirmPass : false,
			loading         : false,
			addressAttached : false,
		};

		this._passwordRef = null;
		this._confirmPassRef = null;

		this.initialValues = {
			FirstName       : '',
			LastName        : '',
			Email           : '',
			MobilePhone     : '',
			Password        : '',
			ConfirmPassword : '',
			agreedToTerms   : false,
			emailMeInfo     : true,
		};
	}

	handleShowPassword = (id) => {
		this.setState((prevState) => ({ [id] : !prevState[id] }), () => {
			switch (id) {
				case 'showPassword':
					this._passwordRef.focus();
					break;
				case 'showConfirmPass':
					this._confirmPassRef.focus();
					break;
				default:
					break;
			}
		});
	}

	formatPhone = (event, setFieldValue) => {
		const { target : { value } } = event;
		if ((/^([\d]{3})-([\d]{3})-([\d]{4})$/gi).test(value)) return;
		let phone = value.replace(/[^\d]/gi, '');
		if (phone.length > 10) {
			phone = phone.substr(0, 10);
		}
		if (phone.length === 10) {
			phone = phone.replace(/^(\d{3})(\d{3})(\d{4})$/gi, '$1-$2-$3');
		}

		setFieldValue('MobilePhone', phone, false);
	}

	formikValidation = async (values) => {
		const errors = {};
		const { agreedToTerms } = values;

		// We are only concerned with checking for errors related to required fields,
		// so we don't need to include a 'then' block
		await SignupSchema.validate(values).catch((err) => {
			if (err.type === 'required') {
				errors.emptyFields = 'Please fill out all fields in the form.';
			}
		});

		// Boolean validation is done here because grouping it into the Yup schema interferes with the
		// generic error message created above
		if (!agreedToTerms) {
			errors.agreedToTerms = 'You must agree to Terms of Service before proceeding';
		}

		return errors;
	}

	logUserIn = (email, password) => (
		new Promise((resolve, reject) => {
			UserService.login({ email, password })
				.then(({ data }) => resolve(data))
				.catch(() => reject());
		})
	)

	attachAddress = (SignUpToken, StreetAddress, City, StateId, Zip) => (
		new Promise((resolve, reject) => {
			SignupService.attachAddress(SignUpToken, {
				StreetAddress,
				City,
				StateId,
				Zip,
			}).then(({ data : user, status : addressStatus }) => resolve({ user, addressStatus }))
				.catch(() => reject());
		})
	)

	handleSubmit = (values) => {
		const {
			accountCreated,
			business,
			signUpToken,
			setContactInfo,
			setAccountCreated,
			setSignUpToken,
			toNextStep,
			login,
			receiveUser,
			history : { push },
		} = this.props;

		const {
			LocationName : CompanyName, StreetAddress, City, StateId, ZipCode,
		} = business;

		const { addressAttached } = this.state;

		const {
			FirstName, LastName, Email, MobilePhone, Password, ConfirmPassword,
		} = values;

		ReactGA.event({
			category : 'RKConnect',
			action   : 'Account creation',
		});

		if (!accountCreated) {
			this.setState({ loading : true });
			setContactInfo(values);
			SignupService.prepaySignup({
				FirstName,
				LastName,
				Email,
				MobilePhone,
				Password,
				ConfirmPassword,
				CompanyName,
				Origin : 'Marketplace',
			}).then(({ data : { SignUpToken }, status }) => {
				if (status === 201) {
					setAccountCreated(true);
					// Store the signup token in marketplace state in case this.attachAddress fails
					setSignUpToken(SignUpToken);
					// Account creation successful, log new user in
					this.logUserIn(Email, Password)
						.then((data) => {
							login();
							receiveUser(data);
							// Logged in, attach address
							this.attachAddress(SignUpToken, StreetAddress, City, StateId, ZipCode)
								.then(({ user, addressStatus }) => {
									if (addressStatus === 201) {
										receiveUser(user);
										this.setState({
											loading         : false,
											addressAttached : true,
										}, toNextStep);
									}
								})
								.catch(() => {
									this.setState({ loading : false });
									utils.notify({
										type    : 'error',
										title   : 'Unable to add contact address',
										message : 'Please try again. If the issue persists, please contact support.',
										timeOut : 2500,
									});
								});
						})
						.catch(() => {
							this.setState({ loading : false });
							utils.notify({
								type    : 'error',
								message : 'An error occurred.',
								timeOut : 2500,
							});
						});
				}
			}).catch(({ response : { data } }) => {
				this.setState({ loading : false });
				if (data.find((e) => e.ErrorCode === 22)) {
					utils.notify({
						type    : 'info',
						title   : 'An account with this email exists',
						message : 'You will be redirected to the login page.',
						timeOut : 2500,
					});
					setTimeout(() => {
						clearTimeout();
						push('/login');
					}, 3000);
				} else if (data.find((e) => e.ErrorCode === 26)) {
					utils.notify({
						type    : 'error',
						title   : 'Another account with this email exists',
						message : 'Please use a different email address.',
						timeOut : 2500,
					});
				} else {
					utils.notify({
						type    : 'error',
						message : 'An error occurred. Please try again.',
						timeOut : 1500,
					});
				}
			});
		} else if (!addressAttached && signUpToken !== null) {
			// We have a signup token saved for the user, but the address wasn't attached before
			this.attachAddress(signUpToken, StreetAddress, City, StateId, ZipCode)
				.then(({ user, addressStatus }) => {
					if (addressStatus === 201) {
						receiveUser(user);
						this.setState({
							loading         : false,
							addressAttached : true,
						}, toNextStep);
					}
				})
				.catch(() => {
					this.setState({ loading : false });
					utils.notify({
						type    : 'error',
						title   : 'Unable to add contact address',
						message : 'Please try again. If the issue persists, please contact support.',
						timeOut : 2500,
					});
				});
		} else {
			toNextStep();
		}
	}

	render() {
		const { JobEventType, accountCreated, toPreviousStep } = this.props;
		const { showPassword, showConfirmPass, loading } = this.state;

		return (
			<div className="form-panel panel panel-default text-center">
				<div className="panel-heading">
					<h4 className="panel-title text-primary">{JobEventType}</h4>
				</div>
				<div className="panel-body container-fluid">
					<div className="row panel-row">
						<h4>Let Us Know Your Contact Information</h4>
					</div>
					<div>
						<Formik
							initialValues={{ ...this.initialValues }}
							validationSchema={SignupSchema}
							validate={this.formikValidation}
							validateOnChange={false}
							validateOnBlur={false}
							onSubmit={this.handleSubmit}
						>
							{({ errors, setFieldValue }) => (
								<Form id="contact-form">
									<small className="col-xs-12 text-danger empty-fields-error">{errors.emptyFields}</small>
									<div className="row">
										<div className="form-group col-sm-6">
											<label htmlFor="FirstName" className="control-label">First Name</label>
											<Field
												name="FirstName"
												id="FirstName"
												className="form-control"
												disabled={accountCreated}
											/>
										</div>
										<div className="form-group col-sm-6">
											<label htmlFor="LastName" className="control-label">Last Name</label>
											<Field
												name="LastName"
												id="LastName"
												className="form-control"
												disabled={accountCreated}
											/>
										</div>
									</div>

									<div className="row">
										<div className="form-group col-sm-6">
											<label htmlFor="Email" className="control-label">Email/Username</label>
											<Field
												name="Email"
												type="email"
												id="Email"
												className="form-control"
												disabled={accountCreated}
											/>
											<small className="text-danger">{!errors.emptyFields && errors.Email}</small>
										</div>
										<div className="form-group col-sm-6">
											<label htmlFor="MobilePhone" className="control-label">Phone Number</label>
											<Field
												name="MobilePhone"
												type="tel"
												placeholder="123-456-7890"
												onBlur={(e) => this.formatPhone(e, setFieldValue)}
												id="MobilePhone"
												className="form-control"
												disabled={accountCreated}
											/>
											<small className="text-danger">{!errors.emptyFields && errors.MobilePhone}</small>
										</div>
									</div>

									<div className="row panel-row">
										<div className="col-xs-12">
											<label htmlFor="Password" className="control-label">Password</label>
											<Field
												name="Password"
												type={showPassword ? 'text' : 'password'}
												id="Password"
												placeholder="Password must be at least 6 characters long"
												className="form-control input-padded-right"
												innerRef={(ref) => { this._passwordRef = ref; }}
												disabled={accountCreated}
											/>
											<small className="text-danger">{!errors.emptyFields && errors.Password}</small>
											<If condition={!accountCreated}>
												<button
													tabIndex="-1"
													type="button"
													id="showPassword"
													className={`btn btn-link rkf ${showPassword ? 'rkf-eye-hidden' : 'rkf-eye'}`}
													onClick={({ target : { id } }) => this.handleShowPassword(id)}
												/>
											</If>
										</div>
									</div>

									<div className="row panel-row">
										<div className="col-xs-12">
											<label htmlFor="ConfirmPassword" className="control-label">Confirm Password</label>
											<Field
												name="ConfirmPassword"
												type={showConfirmPass ? 'text' : 'password'}
												id="ConfirmPassword"
												className="form-control input-padded-right"
												innerRef={(ref) => { this._confirmPassRef = ref; }}
												disabled={accountCreated}
											/>
											<small className="text-danger">{errors.ConfirmPassword}</small>
											<If condition={!accountCreated}>
												<button
													tabIndex="-1"
													type="button"
													id="showConfirmPass"
													className={`btn btn-link rkf ${showConfirmPass ? 'rkf-eye-hidden' : 'rkf-eye'}`}
													onClick={({ target : { id } }) => this.handleShowPassword(id)}
												/>
											</If>
										</div>
									</div>

									<div className="row marketplace-action-buttons-container">
										<button
											type="button"
											className="btn btn-link col-xs-4 col-sm-offset-2 col-xs-offset-1"
											onClick={toPreviousStep}
											disabled={loading}
										>
											Back
										</button>
										<button
											type="submit"
											className="btn btn-primary col-xs-6 col-sm-4"
											disabled={loading}
										>
											{loading ? 'Please Wait...' : 'Next'}
										</button>
									</div>
									<div className="checkbox-group">
										<Field
											name="agreedToTerms"
											render={({ field }) => (
												<input
													{...field /* eslint-disable-line */}
													type="checkbox"
													id="agreedToTerms"
													checked={field.value}
													disabled={accountCreated}
												/>
											)}
										/>
										<label htmlFor="agreedToTerms" className="checkbox-label">
											By signing up, I agree to the&nbsp;
											<a href="https://www.rollkall.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
											.
										</label>
										<br />
										<small className="text-danger">{errors.agreedToTerms}</small>
									</div>
								</Form>
							)}
						</Formik>
					</div>
				</div>
			</div>
		);
	}
}

ContactInfo.propTypes = {
	JobEventType      : PropTypes.string.isRequired,
	accountCreated    : PropTypes.bool.isRequired,
	business          : PropTypes.object.isRequired,
	signUpToken       : PropTypes.string,
	toPreviousStep    : PropTypes.func.isRequired,
	setContactInfo    : PropTypes.func.isRequired,
	setAccountCreated : PropTypes.func.isRequired,
	setSignUpToken    : PropTypes.func.isRequired,
	toNextStep        : PropTypes.func.isRequired,
	history           : PropTypes.object.isRequired,
	login             : PropTypes.func.isRequired,
	receiveUser       : PropTypes.func.isRequired,
};

ContactInfo.defaultProps = {
	signUpToken : null,
};

export default ContactInfo;
